import type {
  ActivityTrackKey,
  TrackConfiguration,
  TrackDefinition,
} from "@/core/activity-levels/track-catalog.js"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"

export type TrackDefinitionWire = TrackDefinition
export type TrackDefinitionInput = TrackConfiguration

export type TrackLevelRewardWire = {
  activityKey: ActivityTrackKey
  level: number
  roleId: string
  createdAt: string
}

export type TrackProgressWire = {
  memberId: string
  displayName: string
  activityKey: ActivityTrackKey
  label: string
  emoji: string
  totalXp: number
  level: number
}

export type TrackLeaderboardEntryWire = TrackProgressWire & {
  rank: number
}

export type AdjustXpOperation = "add" | "remove" | "set" | "reset"
export type AchievementCriterionWire =
  | { kind: "manual" }
  | { kind: "track-level"; activityKey: ActivityTrackKey; level: number }

export type AchievementWire = {
  achievementKey: string
  name: string
  description: string
  emoji: string
  criterion: AchievementCriterionWire
}
export type AchievementDefinitionInput = Omit<AchievementWire, "achievementKey">
export type UserAchievementWire = AchievementWire & {
  unlockedAt: string
  userId: string
}

const readErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const body = (await response.json()) as {
      error?: { message?: string }
      message?: string
    }
    return body.error?.message ?? body.message ?? fallback
  } catch {
    return fallback
  }
}

const jsonHeaders = (): Record<string, string> => {
  const csrfToken = getCsrfTokenFromDocumentCookie()
  if (!csrfToken) {
    throw new Error("Missing CSRF token")
  }
  return {
    "content-type": "application/json",
    "x-csrf-token": csrfToken,
  }
}

const requireCsrf = (): string => {
  const csrfToken = getCsrfTokenFromDocumentCookie()
  if (!csrfToken) {
    throw new Error("Missing CSRF token")
  }
  return csrfToken
}

export const activityLevelsApi = {
  listTracks: async (): Promise<TrackDefinitionWire[]> => {
    const response = await fetch("/api/activity-levels/tracks", {
      method: "GET",
      credentials: "include",
    })
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load activity tracks"),
      )
    }
    const payload = (await response.json()) as {
      items: TrackDefinitionWire[]
    }
    return payload.items
  },
  updateTrack: async (
    activityKey: ActivityTrackKey,
    input: TrackDefinitionInput,
  ): Promise<TrackDefinitionWire> => {
    const response = await fetch(`/api/activity-levels/tracks/${activityKey}`, {
      method: "PUT",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify(input),
    })
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to save activity track"),
      )
    }
    const payload = (await response.json()) as {
      item: TrackDefinitionWire
    }
    return payload.item
  },
  listRewards: async (
    activityKey: ActivityTrackKey,
  ): Promise<TrackLevelRewardWire[]> => {
    const response = await fetch(
      `/api/activity-levels/tracks/${activityKey}/rewards`,
      { method: "GET", credentials: "include" },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load level rewards"),
      )
    }
    const payload = (await response.json()) as {
      items: TrackLevelRewardWire[]
    }
    return payload.items
  },
  addReward: async (
    activityKey: ActivityTrackKey,
    input: { level: number; roleId: string },
  ): Promise<TrackLevelRewardWire> => {
    const response = await fetch(
      `/api/activity-levels/tracks/${activityKey}/rewards`,
      {
        method: "POST",
        credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify(input),
      },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to add level reward"),
      )
    }
    const payload = (await response.json()) as {
      item: TrackLevelRewardWire
    }
    return payload.item
  },
  removeReward: async (
    activityKey: ActivityTrackKey,
    level: number,
    roleId: string,
  ): Promise<void> => {
    const response = await fetch(
      `/api/activity-levels/tracks/${activityKey}/rewards/${level}/${roleId}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: { "x-csrf-token": requireCsrf() },
      },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to remove level reward"),
      )
    }
  },
  getProgress: async (
    activityKey: ActivityTrackKey,
    userId: string,
  ): Promise<TrackProgressWire | null> => {
    const response = await fetch(
      `/api/activity-levels/tracks/${activityKey}/progress/${userId}`,
      { method: "GET", credentials: "include" },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load member progress"),
      )
    }
    const payload = (await response.json()) as {
      item: TrackProgressWire | null
    }
    return payload.item
  },
  adjustXp: async (
    activityKey: ActivityTrackKey,
    userId: string,
    input: { operation: AdjustXpOperation; amount?: number },
  ): Promise<TrackProgressWire> => {
    const response = await fetch(
      `/api/activity-levels/tracks/${activityKey}/progress/${userId}/adjust`,
      {
        method: "POST",
        credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify(input),
      },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to adjust member XP"),
      )
    }
    const payload = (await response.json()) as {
      item: TrackProgressWire
    }
    return payload.item
  },
  getLeaderboard: async (
    activityKey: ActivityTrackKey,
    limit?: number,
  ): Promise<TrackLeaderboardEntryWire[]> => {
    const query = new URLSearchParams()
    if (limit != null) query.set("limit", String(limit))
    const response = await fetch(
      `/api/activity-levels/tracks/${activityKey}/leaderboard${query.size > 0 ? `?${query.toString()}` : ""}`,
      { method: "GET", credentials: "include" },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load leaderboard"),
      )
    }
    const payload = (await response.json()) as {
      items: TrackLeaderboardEntryWire[]
    }
    return payload.items
  },
  listAchievementDefinitions: async (): Promise<AchievementWire[]> => {
    const response = await fetch(
      "/api/activity-levels/achievements/definitions",
      { method: "GET", credentials: "include" },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to load achievement definitions",
        ),
      )
    }
    const payload = (await response.json()) as {
      items: AchievementWire[]
    }
    return payload.items
  },
  upsertAchievementDefinition: async (
    achievementKey: string,
    input: AchievementDefinitionInput,
  ): Promise<AchievementWire> => {
    const response = await fetch(
      `/api/activity-levels/achievements/definitions/${achievementKey}`,
      {
        method: "PUT",
        credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify(input),
      },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to save achievement definition",
        ),
      )
    }
    const payload = (await response.json()) as {
      item: AchievementWire
    }
    return payload.item
  },
  deleteAchievementDefinition: async (
    achievementKey: string,
  ): Promise<void> => {
    const response = await fetch(
      `/api/activity-levels/achievements/definitions/${achievementKey}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: { "x-csrf-token": requireCsrf() },
      },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to delete achievement definition",
        ),
      )
    }
  },
  listUserAchievements: async (
    userId: string,
  ): Promise<UserAchievementWire[]> => {
    const response = await fetch(
      `/api/activity-levels/achievements/users/${userId}`,
      { method: "GET", credentials: "include" },
    )
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load user achievements"),
      )
    }
    const payload = (await response.json()) as {
      items: UserAchievementWire[]
    }
    return payload.items
  },
  grantAchievement: async (
    userId: string,
    achievementKey: string,
  ): Promise<boolean> => {
    const response = await fetch("/api/activity-levels/achievements/grant", {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify({ userId, achievementKey }),
    })
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to grant achievement"),
      )
    }
    const payload = (await response.json()) as {
      granted: boolean
    }
    return payload.granted
  },
  revokeAchievement: async (
    userId: string,
    achievementKey: string,
  ): Promise<boolean> => {
    const response = await fetch("/api/activity-levels/achievements/grant", {
      method: "DELETE",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify({ userId, achievementKey }),
    })
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to revoke achievement"),
      )
    }
    const payload = (await response.json()) as {
      revoked: boolean
    }
    return payload.revoked
  },
}
